/**
 * Emails API
 * Handles email notification settings, manual triggers, and log retrieval
 */
import apiClient from './apiClient';

export interface EmailSettings {
  // Toggle keys are booleans, list-style keys (e.g. excluded_categories,
  // test_recipients) are arrays of strings.
  [key: string]: boolean | string[];
}

export interface EmailSettingsResponse {
  settings: EmailSettings;
}

export interface EmailLog {
  id: number;
  email_type: string;
  recipient_user_id: number | null;
  recipient_name: string | null;
  recipient_email: string | null;
  reference_id: number | null;
  reference_type: string | null;
  reference_date: string | null;
  status: string;
  error_message: string | null;
  sent_at: string | null;
  created_at: string | null;
}

export interface EmailLogsResponse {
  logs: EmailLog[];
  total: number;
  page: number;
  pages: number;
  per_page: number;
}

export interface EmailStats {
  [emailType: string]: { sent: number; failed: number };
}

export interface EmailStatsResponse {
  stats: EmailStats;
}

export interface TriggerResponse {
  message: string;
  result?: any;
}

export interface TestSmtpResponse {
  success: boolean;
  message: string;
  smtp_host?: string;
  from_email?: string;
}

export interface EmailJobSchedule {
  time_ist: string;        // e.g. "10:00"
  default_time: string;    // original default
  day_of_week: string | null; // "mon" or null
  label: string;
  setting_key: string;
}

export interface EmailSchedulesResponse {
  schedules: Record<string, EmailJobSchedule>;
}

export interface SchedulerJobStatus {
  scheduler_running: boolean;
  toggle_on: boolean;
  next_run_ist: string | null;
  next_run_utc: string | null;
}

export interface SchedulerStatusResponse {
  scheduler_running: boolean;
  jobs: Record<string, SchedulerJobStatus>;
}

export const emailsApi = {
  /**
   * Get all email toggle settings
   */
  getSettings: async (): Promise<EmailSettingsResponse> => {
    return apiClient.get<EmailSettingsResponse>('/emails/settings');
  },

  /**
   * Update email toggle settings (batch)
   */
  updateSettings: async (settings: EmailSettings): Promise<{ message: string; updated: string[] }> => {
    return apiClient.put<{ message: string; updated: string[] }>('/emails/settings', { settings });
  },

  /**
   * Manually trigger a specific email job
   */
  triggerEmail: async (emailType: string): Promise<TriggerResponse> => {
    return apiClient.post<TriggerResponse>(`/emails/trigger/${encodeURIComponent(emailType)}`);
  },

  /**
   * Get paginated email logs with optional filters
   */
  getLogs: async (params: {
    page?: number;
    per_page?: number;
    type?: string;
    status?: string;
    search?: string;
  } = {}): Promise<EmailLogsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.per_page) searchParams.set('per_page', String(params.per_page));
    if (params.type) searchParams.set('type', params.type);
    if (params.status) searchParams.set('status', params.status);
    if (params.search) searchParams.set('search', params.search);
    const query = searchParams.toString();
    return apiClient.get<EmailLogsResponse>(`/emails/logs${query ? `?${query}` : ''}`);
  },

  /**
   * Get email send statistics
   */
  getStats: async (): Promise<EmailStatsResponse> => {
    return apiClient.get<EmailStatsResponse>('/emails/logs/stats');
  },

  /**
   * Test SMTP connection by sending a test email
   */
  testSmtp: async (toEmail: string): Promise<TestSmtpResponse> => {
    return apiClient.post<TestSmtpResponse>('/emails/test-smtp', { to_email: toEmail });
  },

  /**
   * Get current IST fire-time for each email job
   */
  getSchedules: async (): Promise<EmailSchedulesResponse> => {
    return apiClient.get<EmailSchedulesResponse>('/emails/schedules');
  },

  /**
   * Update one or more job schedules (times in IST, "HH:MM")
   */
  updateSchedules: async (schedules: Record<string, string>): Promise<{ message: string; updated: string[] }> => {
    return apiClient.put<{ message: string; updated: string[] }>('/emails/schedules', { schedules });
  },

  /**
   * Get live APScheduler status for each email job (next fire time, toggle state)
   */
  getSchedulerStatus: async (): Promise<SchedulerStatusResponse> => {
    return apiClient.get<SchedulerStatusResponse>('/emails/scheduler-status');
  },
};

export default emailsApi;
